import React, { useCallback, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import boTheme from '@ecp/common/src/style/theme/bo';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import { FlexBox, FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import Button from '@ecp/common/src/components/button/bo/Button';

import usePrompt from '@bo-hooks/common/usePrompt';
import { useAlertDialog, useConfirmDialog } from '@bo-hooks/common/useModal';
import menuApi from '@bo-apis/system/menuApi';
import memberApi from '@bo-apis/member/memberApi';
import GridButton from '@bo-components/common/grid/GridButton';
import Grid from '@bo-components/common/grid/Grid';
import { filterList, useCoInfoOptions } from '../options/coInfoConst';

const DetailCoInfo = ({ siteBasicInfo }) => {
  const gridRef = useRef(null);
  const checkRef = useRef(true);
  const [, setToggleToRenderSoBeforeRouterLeave] = useState(false);
  const { showConfirmDialog } = useConfirmDialog();
  const { showAlertDialog } = useAlertDialog();
  const [coMemeberList, setCoMemberList] = useState([]);
  const [coMenuList, setCoMenuList] = useState([]);
  const [coInfoList, setCoInfoList] = useState([]);
  const [typeCode, setTypeCode] = useState('B');

  const saveCoUserStatsSave = async (eventParam) => {
    const textMsg =
      eventParam.row.coUserStatus === 'Y'
        ? `해당 사용자ID를 사용중지 처리하겠습니까?`
        : `해당 사용자ID를 중지해제 처리하겠습니까?`;

    showConfirmDialog(
      textMsg,
      async () => {
        await memberApi.saveCoMember(eventParam.row.coUserId, eventParam.row.coUserStatus === 'Y' ? '30' : '10');
        showAlertDialog('저장되었습니다.');
        if (eventParam.row.coUserStatus === 'Y') {
          eventParam.sheet.setValue(eventParam.row, 'coUserStatusDivisionCode', '30');
        } else {
          eventParam.sheet.setValue(eventParam.row, 'coUserStatusDivisionCode', '10');
        }
      },
      async () => {
        if (eventParam.row.coUserStatus === 'Y') {
          eventParam.sheet.setValue(eventParam.row, 'coUserStatus', 'N');
        } else {
          eventParam.sheet.setValue(eventParam.row, 'coUserStatus', 'Y');
        }
      }
    );
    return;
  };

  const { coMemberOptions, coMenuOptions } = useCoInfoOptions(saveCoUserStatsSave, siteBasicInfo);

  const forceRender = useCallback(() => {
    setToggleToRenderSoBeforeRouterLeave((prev) => !prev);
  }, []);
  usePrompt(gridRef?.current?.hasChangedData() > 0);

  useEffect(() => {
    setTypeCode('B');
    (async () => {
      if (siteBasicInfo.siteNumber) {
        const menuList = await menuApi.getCoMenuInfoList(siteBasicInfo.siteNumber);
        setCoMenuList(menuList.result);
      }
    })();
  }, [siteBasicInfo.siteNumber]);

  const handleRenderFirstFinish = useCallback(() => {
    if (checkRef.current) {
      setCoInfoList(coMenuList);
    } else {
      setCoInfoList(coMemeberList);
    }
  }, [coMenuList, coMemeberList]);

  const handleFilterChanged = async (code) => {
    setTypeCode(code);

    await changeFilterTab(code);
  };

  const changeFilterTab = async (code) => {
    if (code === 'B') {
      await getCoMenuInfoList();
    } else {
      await getCoMemberInfoList();
    }
  };

  const getCoMenuInfoList = useCallback(async () => {
    checkRef.current = true;
    const { result } = await menuApi.getCoMenuInfoList(siteBasicInfo.siteNumber);
    setCoMenuList(result);
  }, [siteBasicInfo.siteNumber]);

  const getCoMemberInfoList = useCallback(async () => {
    checkRef.current = false;
    const { result } = await memberApi.getCoMemberInfoList(siteBasicInfo.siteNumber);
    setCoMemberList(result);
  }, [siteBasicInfo.siteNumber]);

  const handleIdDeleteClick = async () => {
    const checkedRows = gridRef.current?.checkedRows();

    if (checkedRows.length === 0) {
      showAlertDialog('선택된 내역이 없습니다.');
      return;
    }
    const coUserIdList = checkedRows?.map(({ coUserId }) => coUserId);

    showConfirmDialog(`사용자 ID를 삭제하시겠습니까?`, async () => {
      await memberApi.deleteCoMember(coUserIdList);
      showAlertDialog(`사용자 ID가 삭제되었습니다.`);
      await getCoMemberInfoList();
    });
  };

  const handleSaveClick = async () => {
    if (gridRef.current.hasChangedData() <= 0) {
      showAlertDialog('변경된 내역이 없습니다.');
      return;
    } else if (siteBasicInfo.siteStatCode !== '10') {
      showAlertDialog('서비스중인 사이트만 CO ID 생성 가능합니다');
      return;
    }

    const isValid = await gridRef.current.validate();
    if (!isValid) return;

    showConfirmDialog(`저장하시겠습니까?`, saveCoInfo);
  };

  const saveCoInfo = async () => {
    const { created = [], updated = [] } = gridRef.current.getChangedData();

    if (typeCode === 'C') {
      await memberApi.createCoMember(siteBasicInfo.siteNumber, {
        created: created,
        updated: updated,
      });
      showAlertDialog('저장되었습니다.');
      await getCoMemberInfoList();
      return;
    } else {
      await menuApi.saveCoMenuList(siteBasicInfo.siteNumber, {
        created: created,
        updated: updated,
      });
      showAlertDialog('저장되었습니다.');
      await getCoMenuInfoList();
      return;
    }
  };

  const handleResetClick = async () => {
    const checkedRows = gridRef.current?.checkedRows();
    var chkYn = 10;

    checkedRows?.map(({ coUserStatusDivisionCode }) => {
      if (coUserStatusDivisionCode === 40) {
        chkYn = 40;
      } else if (coUserStatusDivisionCode === 20) {
        chkYn = 20;
      }
    });
    if (chkYn === 40) {
      showAlertDialog('ID 상태가 삭제인 경우 비밀번호 초기화할 수 없습니다.');
      return;
    } else if (chkYn === 20) {
      showAlertDialog('ID 상태가 미사용인 경우 비밀번호 초기화할 수 없습니다.');
      return;
    }
    const coUserIdList = checkedRows?.map(({ coUserId }) => coUserId);

    showConfirmDialog(`비밀번호초기화를 하면 사용자ID의 비밀번호가 초기화 됩니다.\n진행하시겠습니까?`, async () => {
      await memberApi.createTempPassword(coUserIdList);
      showAlertDialog(`등록된 휴대전화번호로 임시비밀번호가 발송되었습니다.`);
      await getCoMemberInfoList();
    });
  };

  return (
    <>
      <FlexBox width={'100%'}>
        {filterList.map(({ code, name }) => (
          <FilterButton checked={typeCode === code} onClick={() => handleFilterChanged(code)} key={name}>
            {name}
          </FilterButton>
        ))}
      </FlexBox>
      <Spacing bottom={'20px'} width={'100%'} />
      <Grid
        unPaged
        {...(typeCode === 'C'
          ? {
              showChangeRow: true,
              changeRowLabel: 'ID 삭제',
              showAddRow: true,
              addRowLabel: 'ID 생성',
            }
          : '')}
        data={coInfoList}
        options={typeCode === 'B' ? coMenuOptions : coMemberOptions}
        onDataChange={forceRender}
        onRowAdd={forceRender}
        height={'230px'}
        ref={gridRef}
        changeRowOnClick={handleIdDeleteClick}
        onRenderFirstFinish={handleRenderFirstFinish}
      >
        {typeCode === 'C' && <GridButton label="비밀번호초기화" onClick={handleResetClick} />}
      </Grid>
      <FlexBox justify-content={'flex-end'} width={'100%'}>
        <Button onClick={handleSaveClick}>저장</Button>
      </FlexBox>
    </>
  );
};

export default DetailCoInfo;

DetailCoInfo.propTypes = {
  siteBasicInfo: PropTypes.object,
};

const FilterButton = styled(FlexBoxColumn)`
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 36px;
  font-size: ${boTheme.font.size.small};
  font-weight: ${boTheme.font.weight.regular};
  color: ${boTheme.color.sub6};
  background: ${boTheme.color.sub9};
  border: 1px solid ${boTheme.color.sub7};
  border-bottom: 1px solid ${boTheme.color.sub5};
  cursor: pointer;
  box-sizing: border-box;

  ${(props) =>
    props.checked &&
    css`
      font-weight: ${boTheme.font.weight.bold};
      color: ${boTheme.color.main};
      background: ${boTheme.color.sub10};
      border-top: 3px solid ${boTheme.color.main};
      border-left: 1px solid ${boTheme.color.sub5};
      border-right: 1px solid ${boTheme.color.sub5};
      border-bottom: 0;
    `}
`;
